import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import { DevPhonesPage } from './routes/dev-phones'
import { EventPage } from './routes/event'
import { HomePage } from './routes/home'
import { JoinPage } from './routes/join'
import { PrivacyPage, TermsPage } from './routes/legal'
import { FollowedPage, LinkPage } from './routes/link'
import { OptInPage } from './routes/opt-in'
import { PreviewPage } from './routes/preview'
import { PricingPage, type PricingSearch } from './routes/pricing'
import { RecoverPage } from './routes/recover'
import { TextLogPage, type TextLogSearch } from './routes/texts'
import { UnsubscribePage } from './routes/unsubscribe'

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <Toaster position="bottom-center" richColors closeButton={false} />
    </>
  ),
})

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomePage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/events/$eventId',
    component: function Event() {
      const { eventId } = eventRoute.useParams()
      return <EventPage eventId={eventId} />
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/l/$token',
    component: function Linked() {
      const { token } = linkRoute.useParams()
      return <LinkPage token={token} />
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/join/$code',
    component: function Join() {
      const { code } = joinRoute.useParams()
      return <JoinPage code={code} />
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/dev/phones', component: DevPhonesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/opt-in', component: OptInPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/u/$token',
    component: function Unsubscribe() {
      const { token } = unsubscribeRoute.useParams()
      return <UnsubscribePage token={token} />
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: PrivacyPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/e/$eventId',
    component: function Followed() {
      const { eventId } = followedRoute.useParams()
      return <FollowedPage eventId={eventId} />
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/recover', component: RecoverPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: TermsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/account/texts',
    validateSearch: (search: Record<string, unknown>): TextLogSearch => {
      const event = Number(search.event)
      const month = typeof search.month === 'string' ? search.month : ''
      return {
        ...(Number.isSafeInteger(event) && event > 0 ? { event } : {}),
        ...(/^\d{4}-\d{2}$/.test(month) ? { month } : {}),
        ...(typeof search.purchase === 'string' ? { purchase: search.purchase } : {}),
      }
    },
    component: function Texts() {
      return <TextLogPage search={textsRoute.useSearch()} />
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/pricing',
    validateSearch: (search: Record<string, unknown>): PricingSearch =>
      typeof search.checkout === 'string' ? { checkout: search.checkout } : {},
    component: function Pricing() {
      return <PricingPage search={pricingRoute.useSearch()} />
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/events/$eventId/preview',
    component: function Preview() {
      const { eventId } = previewRoute.useParams()
      return <PreviewPage eventId={eventId} />
    },
  }),
] as const
const eventRoute = routes[1]
const linkRoute = routes[2]
const joinRoute = routes[3]
const unsubscribeRoute = routes[6]
const followedRoute = routes[8]
const textsRoute = routes[11]
const pricingRoute = routes[12]
const previewRoute = routes[13]

const routeTree = rootRoute.addChildren(routes)

export const router = createRouter({ routeTree, defaultPreload: false })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
